'use client';

import React from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { EmblaOptionsType } from 'embla-carousel';
import { NextButton, PrevButton, usePrevNextButtons } from '@/components/search/carousel/EmblaCarouselArrowButton';
import { DotButton, useDotButton } from '@/components/search/carousel/EmblaCarouselDotButton';
import '@/components/search/carousel/css/carousel.css';
import Image from 'next/image';

interface ITravelWithConfidenceCardProps {
  title: string;
  description: string;
  imagePath: string;
}

const TravelWithConfidenceCard = ({ title, description, imagePath }: ITravelWithConfidenceCardProps) => {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-2 bg-gradient-to-b from-[#131919] to-[#090B0B] py-12 px-8 rounded-2xl h-full">
      <Image src={imagePath} alt={title} height={128} width={128} loading="lazy" />
      <h3 className="text-md text-center font-semibold">{title}</h3>
      <p className="text-center text-sm font-light">{description}</p>
    </div>
  );
};

type TravelWithConfidenceItem = {
  title: string;
  description: string;
  imagePath: string;
};

type PropType = {
  items: Array<TravelWithConfidenceItem>;
  options?: EmblaOptionsType;
};

export const TravelWithConfidenceCarousel: React.FC<PropType> = (props) => {
  const { items, options } = props;
  const [emblaRef, emblaApi] = useEmblaCarousel(options);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi);

  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);

  return (
    <div className="embla travel-confidence-embla mt-auto lg:-mt-12">
      <div className="embla__controls">
        <div className="embla__buttons">
          <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
          <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
        </div>
      </div>

      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container travel-confidence-container">
          {items.map((item, index) => (
            <div key={index} className="embla__slide travel-confidence-slide">
              <TravelWithConfidenceCard
                title={item.title}
                description={item.description}
                imagePath={item.imagePath}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="embla__dots">
        {scrollSnaps.map((_, index) => (
          <DotButton
            key={index}
            onClick={() => onDotButtonClick(index)}
            className={'embla__dot'.concat(index === selectedIndex ? ' embla__dot--selected' : '')}
          />
        ))}
      </div>
    </div>
  );
};

export default TravelWithConfidenceCarousel;

